import { createGeoBounds, createLngLat, type GeoBounds, type LngLat } from './coordinates.ts';
import type { Trail } from './trails.ts';

interface TrailSegment {
  readonly start: LngLat;
  readonly end: LngLat;
  readonly length: number;
}

function trailLines(trail: Trail): readonly (readonly LngLat[])[] {
  return trail.geometry.type === 'LineString'
    ? [trail.geometry.coordinates]
    : trail.geometry.coordinates;
}

export function trailCoordinates(trail: Trail): readonly LngLat[] {
  return Object.freeze(trailLines(trail).flat());
}

export function trailBounds(trail: Trail): GeoBounds {
  const coordinates = trailCoordinates(trail);
  const first = coordinates[0];
  if (first === undefined) throw new TypeError('canonical trail geometry is empty');
  let west = first[0];
  let south = first[1];
  let east = first[0];
  let north = first[1];
  for (const coordinate of coordinates.slice(1)) {
    west = Math.min(west, coordinate[0]);
    south = Math.min(south, coordinate[1]);
    east = Math.max(east, coordinate[0]);
    north = Math.max(north, coordinate[1]);
  }
  return createGeoBounds(createLngLat(west, south), createLngLat(east, north));
}

function segments(trail: Trail): readonly TrailSegment[] {
  return trailLines(trail).flatMap((line) =>
    line.slice(1).map((end, index) => {
      const start = line[index];
      if (start === undefined) throw new TypeError('canonical trail geometry is empty');
      const middleLatitude = ((start[1] + end[1]) / 2) * (Math.PI / 180);
      return Object.freeze({
        start,
        end,
        length: Math.hypot((end[0] - start[0]) * Math.cos(middleLatitude), end[1] - start[1]),
      });
    }),
  );
}

export function trailAnchor(trail: Trail): LngLat {
  const coordinates = trailCoordinates(trail);
  const first = coordinates[0];
  if (first === undefined) throw new TypeError('canonical trail geometry is empty');
  const trailSegments = segments(trail);
  const totalLength = trailSegments.reduce((total, segment) => total + segment.length, 0);
  if (totalLength === 0) return first;
  let remaining = totalLength / 2;
  for (const segment of trailSegments) {
    if (remaining <= segment.length && segment.length > 0) {
      const ratio = remaining / segment.length;
      return createLngLat(
        segment.start[0] + (segment.end[0] - segment.start[0]) * ratio,
        segment.start[1] + (segment.end[1] - segment.start[1]) * ratio,
      );
    }
    remaining -= segment.length;
  }
  return coordinates.at(-1) ?? first;
}
